import {
  WSButton,
  WSElement,
  WSPage,
  WSPageToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { WorkLogStatus } from "@wingspanhq/payments/dist/interfaces";
import { useEffect, useMemo, useRef } from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation
} from "react-router-dom";
import { Tabs } from "../../../components/Tabs";
import { useUserId } from "../../../query/hooks/helpers";
import { useQueryWorkLogsSummary } from "../../../query/workLog/queries/useQueryWorkLogsSummary";
import { INVOICING_PATH } from "../../Invoicing";
import { FilterableTableWorkLogsPayeeView } from "../components/TableWorkLogsPayeeView/FilterableTableWorkLogsPayeeView";
import { WorkLogPayeeTabFilterStatus } from "../components/TableWorkLogsPayeeView/filters/types";
import { useModalCreateWorkLog } from "../modals/ModalCreateWorkLog";
import {
  PATH_WORK_LOGS_PAYEE,
  PATH_WORK_LOGS_PAYEE_TAB_APPROVED,
  PATH_WORK_LOGS_PAYEE_TAB_IN_PROGRESS,
  PATH_WORK_LOGS_PAYEE_TAB_REJECTED,
  PATH_WORK_LOGS_PAYEE_TAB_SUBMITTED
} from "../paths";

const useOpenCreateWorkLogModalFromUrl = () => {
  const history = useHistory();
  const location = useLocation();
  const modalCreateWorkLog = useModalCreateWorkLog();
  const hasOpenedModalRef = useRef(false);

  useEffect(() => {
    if (hasOpenedModalRef.current) {
      return;
    }

    const params = new URLSearchParams(location.search);
    const payerId = params.get("payerId");
    const engagementId = params.get("engagementId");

    if (payerId && engagementId) {
      hasOpenedModalRef.current = true;

      // Clear query params from URL
      history.replace(location.pathname);

      modalCreateWorkLog.open({
        defaultPayerId: payerId,
        defaultPayerPayeeEngagementId: engagementId
      });
    }
  }, [history, location.pathname, location.search, modalCreateWorkLog]);
};

export const RoutePayeeLogs = () => {
  const history = useHistory();
  const modalCreateWorkLog = useModalCreateWorkLog();
  const userId = useUserId();

  useOpenCreateWorkLogModalFromUrl();

  // Summary queries for tab counts
  const queryInProgress = useQueryWorkLogsSummary({
    "filter[payeeId]": userId,
    "filter[status][in]": [WorkLogStatus.Draft, WorkLogStatus.InProgress]
  });

  const querySubmitted = useQueryWorkLogsSummary({
    "filter[payeeId]": userId,
    "filter[status][=]": WorkLogStatus.InReview
  });

  const queryApproved = useQueryWorkLogsSummary({
    "filter[payeeId]": userId,
    "filter[status][=]": WorkLogStatus.Approved
  });

  const queryRejected = useQueryWorkLogsSummary({
    "filter[payeeId]": userId,
    "filter[status][=]": WorkLogStatus.Rejected
  });

  const tabs = useMemo(
    () => [
      {
        label: "In progress",
        path: PATH_WORK_LOGS_PAYEE_TAB_IN_PROGRESS,
        badge: queryInProgress.data?.listSize
      },
      {
        label: "Submitted",
        path: PATH_WORK_LOGS_PAYEE_TAB_SUBMITTED,
        badge: querySubmitted.data?.listSize
      },
      {
        label: "Approved",
        path: PATH_WORK_LOGS_PAYEE_TAB_APPROVED,
        badge: queryApproved.data?.listSize
      },
      {
        label: "Rejected",
        path: PATH_WORK_LOGS_PAYEE_TAB_REJECTED,
        badge: queryRejected.data?.listSize
      }
    ],
    [
      queryInProgress.data?.listSize,
      querySubmitted.data?.listSize,
      queryApproved.data?.listSize,
      queryRejected.data?.listSize
    ]
  );

  return (
    <WSPage>
      <WSElement mb="2XL">
        <WSPageToolbar
          title="Work logs"
          actions={[
            {
              label: "New work log",
              buttonKind: "Primary",
              onClick: () => modalCreateWorkLog.open()
            }
          ]}
        />
        <WSText.ParagraphSm color="gray500">
          Track your work and submit for client approval. Approved work logs
          will generate invoices on your{" "}
          <WSButton.Link onClick={() => history.push(INVOICING_PATH)}>
            Invoices
          </WSButton.Link>{" "}
          page.
        </WSText.ParagraphSm>
      </WSElement>

      <Tabs mb="L" tabs={tabs} />

      <Switch>
        <Route path={PATH_WORK_LOGS_PAYEE_TAB_IN_PROGRESS}>
          <FilterableTableWorkLogsPayeeView
            status={WorkLogPayeeTabFilterStatus.InProgress}
          />
        </Route>
        <Route path={PATH_WORK_LOGS_PAYEE_TAB_SUBMITTED}>
          <FilterableTableWorkLogsPayeeView
            status={WorkLogPayeeTabFilterStatus.Submitted}
          />
        </Route>
        <Route path={PATH_WORK_LOGS_PAYEE_TAB_APPROVED}>
          <FilterableTableWorkLogsPayeeView
            status={WorkLogPayeeTabFilterStatus.Approved}
          />
        </Route>
        <Route path={PATH_WORK_LOGS_PAYEE_TAB_REJECTED}>
          <FilterableTableWorkLogsPayeeView
            status={WorkLogPayeeTabFilterStatus.Rejected}
          />
        </Route>
        <Redirect
          from={PATH_WORK_LOGS_PAYEE}
          to={PATH_WORK_LOGS_PAYEE_TAB_IN_PROGRESS}
        />
      </Switch>
    </WSPage>
  );
};
